/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  FlexContainer,
  Text,
  Row,
  Col,
  theme,
  SVGIcon,
  Popover,
  Navigation,
  ModalConfirm,
  Hidden,
} from "@nordcloud/gnui";
import { EnvironmentType, useEnvironmentLazyQuery } from "~/generated/graphql";
import { BudgetChart, Co2eSavings, WasteIndicator } from "~/components";
import { getApplicationStatus } from "~/components/ApplicationItem";
import { getBudgetValue } from "~/components/Budgets";
import { dateFormat, ERROR_TEXT } from "~/constants";
import { useCurrency } from "~/hooks";
import { useRemoveEnvironment } from "~/hooks/graphql";
import { IEnvironment } from "~/models";
import { ROUTES } from "~/routing/routes";
import { Can, ACTIONS } from "~/services/auth/RBAC";
import { showError, showSuccess } from "~/services/toast";
import {
  formatMoneyWithFallback,
  generateActionConfirmText,
  generateActionSuccessText,
  handleError,
  isNotNil,
  toInputError,
} from "~/tools";
import { EnvironmentSidebar } from "~/views/applications/components/EnvironmentSidebar";
import { types } from "~/views/applications/types";
import { EnvironmentInput } from "~/views/environments/EnvironmentInput";
import { useEnvironments } from "./hooks";
import { getNumFromString, mapEnvironmentData } from "./utils";

type Props = {
  environment: ReturnType<typeof useEnvironments>["environments"][number];
  appId: string;
};

// TODO: split to smaller components
export function EnvironmentItem({ environment, appId }: Props) {
  const [isModalConfirmOpen, setIsModalConfirmOpen] = React.useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [initialValues, setInitialValues] = React.useState<IEnvironment>({
    nid: "",
    applicationId: appId,
    name: "",
    estateId: [],
    type: EnvironmentType.Other,
    ownerId: "",
    description: "",
    contactIds: [],
    budget: undefined,
  });

  const [removeEnvironment] = useRemoveEnvironment();

  const [
    getEnvironment,
    { data: environmentData, loading: isEnvironmentDataLoading },
  ] = useEnvironmentLazyQuery({ fetchPolicy: "no-cache" });

  React.useEffect(() => {
    const type =
      types[
        types.indexOf(
          environmentData?.environment?.environmentType ?? EnvironmentType.Other
        )
      ];

    setInitialValues((prevValues) => ({
      ...prevValues,
      applicationId: appId,
      estateId: [],
      type,
      ...mapEnvironmentData(environmentData),
    }));
  }, [environmentData, appId]);

  const { currency } = useCurrency();

  const onEditHandler = React.useCallback(async () => {
    try {
      await getEnvironment({
        variables: {
          id: environment.id,
          year: dayjs().format(dateFormat.year),
        },
      });
      setIsSidebarOpen(true);
    } catch {
      showError(ERROR_TEXT.default);
    }
  }, [getEnvironment, setIsSidebarOpen, environment.id]);

  const onRemoveHandler = async () => {
    try {
      setIsModalConfirmOpen(false);

      await removeEnvironment({ nid: environment.id });

      showSuccess(
        generateActionSuccessText(`The ${environment.environment}`)()(
          "deleted"
        )()
      );
    } catch (error) {
      handleError(toInputError(error));
    }
  };

  const currentMonthIndex = dayjs().month();

  const forecast = getNumFromString(environment.forecast);
  const budget = parseFloat(
    environment.budget.budgetByMonth[currentMonthIndex] ?? "0"
  );
  const progress = budget !== 0 ? (100 * forecast) / budget : 0;
  const status = getApplicationStatus(progress);

  const co2eSavings = parseFloat(environment.cloudWasteAggregate?.co2e ?? "0");

  return (
    <>
      <Box boxStyle="lightGrey" innerSpacing="spacing03" spacing="spacing02">
        <Row>
          <Col sm={5} md={4} lg={6} style={{ justifyContent: "center" }}>
            <FlexContainer>
              <Hidden xs>
                <BudgetChart
                  progress={progress}
                  color={status.color}
                  size={60}
                  strokeWidth={9}
                  hasBudget={isNotNil(budget) && budget > 0}
                  icon="cloud"
                />
              </Hidden>
              <div>
                <Text
                  nowrap
                  size="lg"
                  weight="medium"
                  tag="div"
                  width="12rem"
                  mb={theme.spacing.spacing01}
                  ml={theme.spacing.spacing04}
                >
                  <Link
                    to={`${ROUTES.applications.index}/${appId}/${environment.id}`}
                  >
                    {environment.environment}
                  </Link>
                </Text>
                <FlexContainer>
                  <Text size="sm" tag="span" ml={theme.spacing.spacing04}>
                    {environment.environmentType.toLowerCase()}
                  </Text>
                </FlexContainer>
              </div>
              <div
                style={{
                  paddingLeft: theme.spacing.spacing04,
                  borderLeft: `1px solid ${theme.color.border.border01}`,
                }}
              >
                <Text
                  isTitle
                  size="sm"
                  tag="div"
                  mb={theme.spacing.spacing01}
                  lineHeight="1.5rem"
                >
                  Environment belongs to Organizational Units:
                </Text>

                <EnvironmentInput environmentId={environment.id} />
              </div>
            </FlexContainer>
          </Col>
          <Col sm={3} md={4} lg={6} style={{ justifyContent: "center" }}>
            <FlexContainer justifyContent="flex-end">
              <Hidden xs sm>
                <div
                  css={{
                    width: "9rem",
                    paddingLeft: theme.spacing.spacing04,
                    marginRight: `${theme.spacing.spacing02}`,
                  }}
                >
                  <Text
                    isTitle
                    nowrap
                    size="sm"
                    tag="div"
                    mb={theme.spacing.spacing01}
                    align="right"
                  >
                    Estate Records
                  </Text>

                  <FlexContainer justifyContent="flex-end" alignItems="end">
                    <Text
                      tag="div"
                      ml={theme.spacing.spacing02}
                      weight="medium"
                      lineHeight={theme.spacing.spacing06}
                    >
                      {environment.resources}
                    </Text>
                  </FlexContainer>
                </div>
              </Hidden>
              <div
                style={{
                  width: "9rem",
                  paddingLeft: theme.spacing.spacing04,
                  borderLeft: `1px solid ${theme.color.border.border01}`,
                  marginRight: `${theme.spacing.spacing02}`,
                }}
              >
                <Text
                  isTitle
                  nowrap
                  size="sm"
                  tag="div"
                  mb={theme.spacing.spacing01}
                  lineHeight={theme.spacing.spacing05}
                  align="right"
                >
                  Monthly Costs
                </Text>

                <FlexContainer justifyContent="flex-end" alignItems="end">
                  <Text
                    tag="div"
                    ml={theme.spacing.spacing02}
                    weight="medium"
                    lineHeight={theme.spacing.spacing06}
                  >
                    {environment.actualCosts}
                  </Text>
                </FlexContainer>
              </div>
              <Hidden xs sm>
                <div
                  style={{
                    width: "14rem",
                    paddingLeft: theme.spacing.spacing04,
                    borderLeft: `1px solid ${theme.color.border.border01}`,
                    marginRight: `${theme.spacing.spacing02}`,
                  }}
                >
                  <Text
                    isTitle
                    nowrap
                    size="sm"
                    tag="div"
                    mb={theme.spacing.spacing01}
                    align="right"
                    lineHeight={theme.spacing.spacing06}
                  >
                    Monthly Forecast
                  </Text>
                  <FlexContainer justifyContent="flex-end" alignItems="center">
                    <When condition={environment.cloudWasteAggregate?.hasWaste}>
                      <Co2eSavings iconOnly co2eSavings={co2eSavings} />
                      <WasteIndicator
                        size="sm"
                        reason="Environment has Savings Suggestions"
                        amount={{
                          amount: environment.cloudWasteAggregate?.cost ?? "",
                          uri: `${ROUTES.applications.index}/${appId}/${environment.id}?page=0&waste=true`,
                        }}
                      />
                    </When>
                    <Text
                      tag="div"
                      ml={theme.spacing.spacing02}
                      weight="medium"
                      lineHeight={theme.spacing.spacing06}
                    >
                      {environment.forecast}
                    </Text>
                  </FlexContainer>
                </div>
                <div
                  style={{
                    width: "13rem",
                    paddingLeft: theme.spacing.spacing04,
                    borderLeft: `1px solid ${theme.color.border.border01}`,
                    marginRight: `${theme.spacing.spacing02}`,
                    borderRight: `1px solid ${theme.color.border.border01}`,
                  }}
                >
                  <div style={{ paddingRight: theme.spacing.spacing02 }}>
                    <Text
                      isTitle
                      nowrap
                      size="sm"
                      tag="div"
                      mb={theme.spacing.spacing01}
                      align="right"
                      lineHeight={theme.spacing.spacing06}
                    >
                      Monthly Budget
                    </Text>
                  </div>

                  <FlexContainer justifyContent="flex-end" alignItems="end">
                    <div style={{ paddingRight: theme.spacing.spacing02 }}>
                      <Text
                        tag="div"
                        ml={theme.spacing.spacing02}
                        weight="medium"
                        lineHeight={theme.spacing.spacing06}
                      >
                        {formatMoneyWithFallback(
                          getBudgetValue(
                            environment.budget.budgetByMonth,
                            currentMonthIndex
                          ),
                          currency
                        )}
                      </Text>
                    </div>
                  </FlexContainer>
                </div>
              </Hidden>
              <div
                css={{
                  width: "2rem",
                  paddingLeft: theme.spacing.spacing01,
                }}
              >
                <Popover
                  alignRight
                  trigger={<Button severity="low" icon="menu" size="md" />}
                >
                  <Navigation.Container popoverMenu>
                    <Can action={ACTIONS.updateEnvironment}>
                      <Navigation.Item onClick={onEditHandler}>
                        <SVGIcon name="edit" />
                        <Text tag="span">Edit Environment</Text>
                      </Navigation.Item>
                    </Can>
                    <Can action={ACTIONS.removeEnvironment}>
                      <Navigation.Item
                        onClick={() => setIsModalConfirmOpen(true)}
                      >
                        <SVGIcon name="trash" />
                        <Text tag="span">Delete</Text>
                      </Navigation.Item>
                    </Can>
                  </Navigation.Container>
                </Popover>
              </div>
            </FlexContainer>
          </Col>
        </Row>
      </Box>
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={onRemoveHandler}
        actionLabel="Delete"
        contentLabel="Delete Environment"
        onClose={() => setIsModalConfirmOpen(false)}
      >
        {generateActionConfirmText(environment.environment)("delete")()}
      </ModalConfirm>
      <EnvironmentSidebar
        setIsSidebarOpen={setIsSidebarOpen}
        isLoading={isEnvironmentDataLoading}
        isSidebarOpen={isSidebarOpen}
        initialValues={initialValues}
      />
    </>
  );
}
